import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@repere/api-client';
import { CODE_ABONNEMENT_REQUIS } from '@repere/core';
import { useEffect, useState, type ReactNode } from 'react';
import { InvitationAbonnement } from './InvitationAbonnement';

const CODES = [CODE_ABONNEMENT_REQUIS];

/**
 * Ouvre l'invitation à souscrire dès qu'une écriture est refusée faute d'abonnement.
 *
 * Écouter le cache de mutations plutôt que d'ajouter le même `onError` dans chaque
 * formulaire : le refus peut venir du journal, des dépenses, des documents, de la fiche
 * d'un enfant. Recopier la règle à six endroits garantit qu'elle finira par manquer au
 * septième, et un parent y retrouverait alors le mur qu'on vient d'enlever.
 *
 * La reconnaissance se fait sur le **code** renvoyé par l'API, jamais sur le message :
 * comparer des phrases françaises casserait à la première reformulation.
 *
 * Elle ne s'ouvre que sur une action du parent — jamais au chargement d'un écran. Une
 * sollicitation qui arrive sans qu'on ait rien demandé est une publicité.
 */
export function SurveillanceAbonnement({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState<string | null>(null);

  useEffect(() => {
    const cache = queryClient.getMutationCache();
    return cache.subscribe((evenement) => {
      const erreur: unknown = evenement.mutation?.state.error;
      if (erreur instanceof ApiError && CODES.includes(erreur.code)) setCode(erreur.code);
    });
  }, [queryClient]);

  return (
    <>
      {children}
      {code ? <InvitationAbonnement onFermer={() => setCode(null)} /> : null}
    </>
  );
}
